import { useEffect, useRef, useState } from "react";
import { FileText } from "lucide-react";
import { TableSkeleton } from "@/components/Loading";
import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardHeader } from "@/components/ui/card";
import { screens } from "../screens";
import type { ReportItem } from "../../shared/api";
import { formatAuditTime } from "../../shared/audit";
import { ErrorAlert } from "@/components/ErrorAlert";
import { Button } from "@/components/ui/button";
import { Pager } from "@/components/Pager";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { createReport, errorText, getReportPdf, getReports } from "@/lib/client";

async function save(id: number) {
  const { blob, fileName } = await getReportPdf(id);
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement("a"), { href: url, download: fileName }).click();
  URL.revokeObjectURL(url);
}

/** E7 (spec §6): one button makes the report and downloads it; past reports are listed and stay downloadable. Nothing is deleted. */
export function ReportScreen() {
  const [items, setItems] = useState<ReportItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const makeBtn = useRef<HTMLButtonElement>(null);
  const [downloading, setDownloading] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    getReports(page).then((r) => { if (live) { setItems(r.items); setTotal(r.total); setPageSize(r.pageSize); } }, (e) => live && setError(errorText(e)));
    return () => { live = false; };
  }, [page]);

  async function download(id: number) {
    setError(""); setDownloading(id);
    try { await save(id); } catch (e) { setError(errorText(e)); } finally { setDownloading(null); }
  }
  async function make() {
    setBusy(true); setError(""); setNotice("");
    try {
      const { reportId } = await createReport();
      setNotice(`Rapor no ${reportId} oluşturuldu.`);
      if (page === 1) { const r = await getReports(1); setItems(r.items); setTotal(r.total); setPageSize(r.pageSize); } else setPage(1);
      await save(reportId);
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }

  return (
    <main className="flex flex-col gap-6 p-6">
      <PageHeader title="Denetim Raporu" description={screens.find((s) => s.id === "E7")!.description}
        actions={<Button ref={makeBtn} disabled={busy} onClick={() => void make()}>{busy ? "Oluşturuluyor…" : "Raporu oluştur (PDF)"}</Button>} />
      <p className="text-sm text-muted-foreground">Rapor, yüklenen son versiyonlara ve son mutabakata göre hazırlanır. Her oluşturma yeni bir rapor numarası alır; rapor kalıcı olarak saklanır ve silinemez.</p>
      {notice && (
        <Alert variant="success" role="status">
          <AlertDescription className="flex flex-row items-center justify-between gap-4 text-success-foreground">
            <span><span aria-hidden="true">✓ </span>{notice}</span>
            <Button size="sm" variant="outline" onClick={() => { setNotice(""); makeBtn.current?.focus(); }}>Kapat</Button>
          </AlertDescription>
        </Alert>
      )}
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {!items && !error && <div aria-busy="true"><TableSkeleton rows={4} cols={4} /></div>}
      {items && (
        <Card className="gap-0 overflow-clip p-0">
          <CardHeader className="border-b px-4 py-3 [.border-b]:pb-3"><h2 className="text-base font-semibold">Geçmiş raporlar</h2></CardHeader>
          {items.length === 0 ? (
            <EmptyState icon={FileText} title="Henüz rapor oluşturulmadı">&quot;Raporu oluştur&quot; düğmesiyle ilk raporu alın.</EmptyState>
          ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground w-32 pl-4">Rapor</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground w-48">Zaman</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground">Oluşturan</TableHead><TableHead className="bg-muted/60 text-xs font-medium text-muted-foreground"><span className="sr-only">İndir</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.reportId}>
                <TableCell className="pl-4"><span className="flex items-center gap-2"><FileText aria-hidden="true" className="size-4 text-muted-foreground" /><span className="tabular-nums">Rapor no {r.reportId}</span></span></TableCell>
                <TableCell className="whitespace-nowrap tabular-nums">{formatAuditTime(r.createdAt)}</TableCell>
                <TableCell><span className="flex items-center gap-2"><UserAvatar name={r.createdBy} />{r.createdBy}</span></TableCell>
                <TableCell className="pr-4 text-right"><Button size="sm" variant="outline" aria-label={`Rapor no ${r.reportId} ${downloading === r.reportId ? "İndiriliyor…" : "PDF indir"}`} disabled={downloading === r.reportId} onClick={() => void download(r.reportId)}>{downloading === r.reportId ? "İndiriliyor…" : "PDF indir"}</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
          )}
          {total > 0 && <div className="border-t px-4 py-2"><Pager page={page} total={total} pageSize={pageSize} onPage={setPage} label="Rapor sayfalama" showTotal /></div>}
        </Card>
      )}
    </main>
  );
}
